import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { clearAuthCache } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

/* Set a new password from the emailed reset link (?token=...). Signs the user in on success. */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const isEs = (navigator.language || '').toLowerCase().startsWith('es');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw1.length < 8) { setErr(isEs ? 'Mínimo 8 caracteres.' : 'Minimum 8 characters.'); return; }
    if (pw1 !== pw2) { setErr(isEs ? 'Las contraseñas no coinciden.' : 'Passwords do not match.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/reset-confirm`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: pw1 }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof j.detail === 'string' ? j.detail : (isEs ? 'No se pudo cambiar la contraseña.' : 'Could not change the password.')); setBusy(false); return; }
      clearAuthCache();
      try { localStorage.setItem('kx_login_email', j.email || ''); } catch { /* noop */ }
      setDone(true);
      setTimeout(() => navigate('/hub', { replace: true }), 1600);
    } catch { setErr(isEs ? 'Error de conexión — inténtalo de nuevo.' : 'Connection error — please try again.'); setBusy(false); }
  };

  const field = 'mt-1.5 w-full h-12 rounded-xl bg-white border border-neutral-300 px-4 pr-11 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-[#F77F11] transition-colors';

  return (
    <div className="min-h-screen bg-[#F6F7F9] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-3xl bg-white border border-neutral-200 shadow-xl p-8 sm:p-10">
        <img src="/kitxens-logo.png" alt="Kitxens" className="h-9 w-auto" />
        {done ? (
          <div className="mt-6 text-center" data-testid="reset-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <h1 className="mt-3 text-xl font-bold text-neutral-900">{isEs ? 'Contraseña actualizada' : 'Password updated'}</h1>
            <p className="mt-1 text-[13px] text-neutral-500">{isEs ? 'Sesión iniciada — te llevamos a tu Hub…' : 'You are signed in — taking you to your Hub…'}</p>
          </div>
        ) : (
          <>
            <h1 className="mt-4 text-2xl font-bold text-neutral-900">{isEs ? 'Crea tu nueva contraseña' : 'Set your new password'}</h1>
            <p className="mt-1 text-[13.5px] text-neutral-500">{isEs ? 'Mínimo 8 caracteres. Al guardar entras directamente.' : 'Minimum 8 characters. You are signed in right after saving.'}</p>
            {!token && (
              <p className="mt-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[12.5px] font-semibold text-red-600" data-testid="reset-no-token">
                {isEs ? 'Enlace incompleto — vuelve al email y pulsa el botón de nuevo.' : 'Incomplete link — go back to the email and tap the button again.'}
              </p>
            )}
            <form onSubmit={submit} className="mt-6">
              <label className="text-[11px] font-semibold tracking-wide uppercase text-neutral-500">{isEs ? 'Nueva contraseña' : 'New password'}</label>
              <div className="relative">
                <input type={show ? 'text' : 'password'} value={pw1} onChange={(e) => { setPw1(e.target.value); setErr(''); }} autoFocus
                  placeholder="••••••••" className={field} data-testid="reset-password-input" />
                <button type="button" onClick={() => setShow((v) => !v)} aria-label="toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-neutral-400 hover:text-neutral-700" data-testid="reset-password-toggle">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <label className="mt-4 block text-[11px] font-semibold tracking-wide uppercase text-neutral-500">{isEs ? 'Repite la contraseña' : 'Repeat password'}</label>
              <input type={show ? 'text' : 'password'} value={pw2} onChange={(e) => { setPw2(e.target.value); setErr(''); }}
                placeholder="••••••••" className={field} data-testid="reset-password-confirm-input" />
              {err && (
                <div className="mt-3" data-testid="reset-error-box">
                  <p className="text-[12.5px] text-red-500" data-testid="reset-error">{err}</p>
                  <p className="mt-1 text-[11.5px] text-neutral-400">
                    {isEs ? '¿Sigues sin poder entrar? Escríbenos a ' : 'Still locked out? Email us at '}
                    <a href="mailto:support@kitxens.com" className="font-semibold text-[#F77F11] hover:underline">support@kitxens.com</a>
                  </p>
                </div>
              )}
              <button type="submit" disabled={busy || !token}
                className="mt-5 w-full h-12 rounded-xl bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2"
                data-testid="reset-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {isEs ? 'Guardar y entrar' : 'Save and sign in'}
              </button>
              <p className="mt-4 text-center text-[12px] text-neutral-400">
                <a href="/login" className="hover:text-[#F77F11] font-semibold" data-testid="reset-back-login">{isEs ? '← Volver al login' : '← Back to login'}</a>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
